/**
 * Leitores puros dos registros de histórico de preço (imóvel e condomínio).
 *
 * Extraídos de `pages/AreaQprecoIndex.vue`: as cadeias de fallback entre
 * `snake_case`/`camelCase` e as regras de redação de fonte externa continuam
 * exatamente iguais.
 */
import { formatNumber, numericValue, presentValue } from './qprecoFormatters';

export const historyPrice = item =>
  presentValue(
    item?.price,
    item?.condoPrice,
    item?.condo_price,
    item?.condominiumPrice,
    item?.condominium_price,
    item?.valor_condominio,
    item?.condominium_amount,
    item?.condominiumFeeAmount,
    item?.condominium_fee_amount,
    item?.valor_venda,
    item?.valor_aluguel,
    item?.currentPrice,
    item?.current_price,
    item?.previousPrice,
    item?.previous_price
  );

export const historyPricePerSquareMeter = item =>
  presentValue(
    item?.pricePerSquareMeter,
    item?.price_per_square_meter,
    item?.valor_m2,
    item?.price_per_sqm
  );

export const historyEventDate = item =>
  presentValue(
    item?.eventDate,
    item?.event_date,
    item?.lastTimeOnMarket,
    item?.last_time_on_market,
    item?.recorded_at,
    item?.updated_at,
    item?.created_at,
    item?.data,
    item?.date
  );

export const historySourceId = item =>
  presentValue(item?.sourceId, item?.source_id, item?.external_id, item?.id);

export const isRedactedHistorySource = item =>
  Boolean(item?.redacted || item?.external_source || item?.externalSource);

export const historySourceUrl = (item, { redactExternal = false } = {}) => {
  if (redactExternal && isRedactedHistorySource(item)) return '';

  return presentValue(
    item?.sourceUrl,
    item?.source_url,
    item?.link_externo,
    item?.url
  );
};

export const historyAddress = item => {
  const address = presentValue(
    item?.address,
    item?.endereco,
    item?.logradouro,
    item?.street
  );
  const number = presentValue(
    item?.streetNumber,
    item?.street_number,
    item?.numero,
    item?.number
  );
  const complement = presentValue(
    item?.complement,
    item?.complemento,
    item?.address_complement
  );
  const neighborhood = presentValue(item?.bairro, item?.neighborhood);
  const city = presentValue(item?.cidade, item?.city);
  const uf = presentValue(item?.uf, item?.state);

  const addressWithNumber =
    address && number && !String(address).includes(String(number))
      ? `${address}, ${number}`
      : address;
  const cityUf = [city, uf].filter(Boolean).join(' - ');

  return [addressWithNumber, complement, neighborhood, cityUf]
    .filter(Boolean)
    .join(' · ');
};

export const historyKindLabel = item => {
  const labels = {
    asking_price: 'Anúncio encontrado',
    crm_price_change: 'Alteração cadastrada',
    deed_transaction: 'Transação registrada',
    internal_listing: 'Imóvel na base interna',
    local_price_change: 'Alteração na Imudei',
    condominium_price_history: 'Negociação no condomínio',
    outside_house: 'Fora da casa',
    price_reduction: 'Redução de preço',
    registry_snapshot: 'Registro público',
  };
  return labels[item?.kind] || item?.transactionLabel || 'Histórico';
};

export const historySourceLabel = (item, { redactExternal = false } = {}) => {
  if (redactExternal && isRedactedHistorySource(item)) {
    return 'Fonte externa redigida';
  }

  return (
    item?.sourcePortal ||
    item?.source_portal ||
    item?.portal ||
    item?.source ||
    'Base integrada Imudei'
  );
};

export const condominiumHistoryDescription = item => {
  const area = presentValue(
    item?.totalArea,
    item?.total_area,
    item?.area_util,
    item?.area
  );
  const bedrooms = presentValue(
    item?.bedroomCount,
    item?.bedroom_count,
    item?.quartos,
    item?.bedrooms
  );
  const parking = presentValue(
    item?.parkingSlots,
    item?.parking_slots,
    item?.vagas_garagem,
    item?.vagas
  );
  const bedroomLabel = numericValue(bedrooms) === 1 ? 'quarto' : 'quartos';
  const parkingLabel = numericValue(parking) === 1 ? 'vaga' : 'vagas';

  return [
    area ? `${formatNumber(area)} m²` : null,
    bedrooms !== undefined && bedrooms !== null
      ? `${formatNumber(bedrooms)} ${bedroomLabel}`
      : null,
    parking !== undefined && parking !== null
      ? `${formatNumber(parking)} ${parkingLabel}`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
};
